'use client';

import React, { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  const t = useTranslations('common');

  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      timestamp: new Date().toISOString(),
    });

    // You can add error reporting service here (e.g., Sentry, LogRocket, etc.)
    // Example: Sentry.captureException(error);
  }, [error]);

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center py-5" role="main">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8 text-center">
            <h1 
              className="display-1 fw-bold mb-4"
              aria-label="Error 500"
            >
              <span className="text-gradient-danger">500</span>
            </h1>
            
            <h2 className="fs-3 mb-3">
              <span className="text-danger" aria-hidden="true">
                {t('global.error.oops')}
              </span>{' '}
              {t('global.error.title')}
            </h2>
            
            <p className="lead text-body mb-4">
              {t('global.error.message')}
            </p>

            {/* Error details for development */}
            {process.env.NODE_ENV === 'development' && (
              <details className="text-start mb-4 p-3 bg-dark rounded">
                <summary className="text-warning cursor-pointer mb-2">
                  Technical Details
                </summary>
                <pre className="text-muted small overflow-auto">
                  {error.message}
                  {error.stack && '\n\nStack trace:\n' + error.stack}
                </pre>
              </details>
            )}
            
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <button 
                onClick={() => reset()} 
                className="btn btn-gradient d-inline-flex align-items-center justify-content-center"
                aria-label={t('global.error.tryAgain')}
              >
                <i className="bx bx-refresh me-2" aria-hidden="true"></i>
                {t('global.error.tryAgain')}
              </button>
              
              <Link 
                href="/" 
                className="btn btn-outline-primary d-inline-flex align-items-center justify-content-center"
                aria-label={t('global.error.goHome')}
              >
                <i className="bx bx-home me-2" aria-hidden="true"></i>
                {t('global.error.goHome')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 